import { XRTodo, IApp } from 'matrix-requirements-sdk/client';
import { Todo } from 'matrix-requirements-sdk/client';
import { sdkInstance } from './Instance';

export interface IRenderCallback {
  (list: Todo[]): void;
}

export interface IFilterCallback {
  (todo: Todo, index: number): boolean;
}

/**
 * PagedLocalTodos offers a paging interface over a possibly very large set of local objects.
 * It supports two kinds of filters
 *   1) through setFilteredElements(), where you can pass a subset of the full database in according to your own
 *      criteria, and
 *   2) a search text filter which looks at the (possibly already filtered) items for a matching string in
 *      the itemref, item title, assignee or originator or description of the todo
 *
 * There are methods for paging and assisting with UI for paging, like whether or not prev/next buttons
 * should be enabled.
 */
export class PagedLocalTodos {
  private pageStart: number = 0;
  private pageLength: number = 100;

  private titleCache: {} = {};
  private currentSearchValue: string = '';
  private elementsLst: Todo[] = [];
  private filteredElementsLst: Todo[] = [];
  private searchFilteredElementsLst: Todo[] = [];
  private renderCallback: IRenderCallback = () => {};

  /**
   * The renderCallback will be made as the search filters are set or the change page methods are called.
   * @param pageLength
   * @param renderCallback
   */
  constructor(pageLength: number, renderCallback: IRenderCallback) {
    this.pageLength = pageLength;
    this.renderCallback = renderCallback;
  }

  /**
   *
   * @returns the full element database.
   */
  public getAllElements(): Todo[] {
    return this.elementsLst;
  }

  public getFilteredElements(): Todo[] {
    return this.filteredElementsLst;
  }

  public getSearchFilteredElements(): Todo[] {
    return this.searchFilteredElementsLst;
  }

  public getPageStart(): number {
    return this.pageStart;
  }

  public getPageLength(): number {
    return this.pageLength;
  }

  public setAllElements(list: Todo[]) {
    // Getting application titles are very expensive, but we need this for the text box search.
    // Cache that information for the whole list here.
    const that = this;
    const app = <IApp>sdkInstance.app;
    list.forEach(
      (todo) =>
        (that.titleCache[todo.itemRef] = app.getItemTitle(todo.itemRef)),
    );

    this.elementsLst = list;
    this.filteredElementsLst = this.elementsLst;
    this.runSearchFilter(this.filteredElementsLst);
  }

  /**
   * Filter elements cumulatively. Repeated calls will continually narrow the filtered results.
   * call clearFilteredElements() to reset the filter back to the full element list.
   * @param callback - a callback like you'd use in Array.filter().
   */
  public cumulativeFilterElements(callback: IFilterCallback) {
    const filteredList = this.filteredElementsLst.filter(callback);
    this.setFilteredElements(filteredList);
  }

  private setFilteredElements(list: Todo[]) {
    this.filteredElementsLst = list;
    // We have to re-run the search filter if there is anything.
    this.runSearchFilter(this.filteredElementsLst);
  }

  public clearFilteredElements() {
    this.setFilteredElements(this.elementsLst);
  }

  private setSearchFilteredElements(list: Todo[]) {
    this.searchFilteredElementsLst = list;
  }

  // run the search filter on the current search value. Set the list of filtered elements too.
  private runSearchFilter(sourceList: Todo[]) {
    if (this.currentSearchValue && this.currentSearchValue.length > 0) {
      let newList = sourceList.filter((v) => {
        return (
          this.todoToString(v)
            .toLowerCase()
            .indexOf(this.currentSearchValue.toLowerCase()) >= 0
        );
      });
      this.setSearchFilteredElements(newList);
    } else {
      this.setSearchFilteredElements(sourceList);
    }
  }

  public updateSearchFilter(value: string) {
    let lstToSearch = this.filteredElementsLst;
    if (
      this.currentSearchValue &&
      value.indexOf(this.currentSearchValue) === 0
    ) {
      // Happily, we can search in the current (smaller) filter set, which is more efficient.
      // Because value is just extending the oldValue, therefore, narrowing the search.
      // TODO(mike).
      lstToSearch = this.searchFilteredElementsLst;
    }

    this.currentSearchValue = value;
    this.runSearchFilter(lstToSearch);

    this.setPageStart(0);
  }

  public setPageStart(index: number) {
    this.pageStart = index;
    // We give the display callback precisely those elements it needs to display a page and no more.
    const pageSlice = this.getCurrentPageData();
    this.renderCallback(pageSlice);
  }

  public getCurrentPageData(): Todo[] {
    const end = Math.min(
      this.searchFilteredElementsLst.length,
      this.pageStart + this.pageLength,
    );
    return this.searchFilteredElementsLst.slice(this.pageStart, end);
  }

  private todoToString(t: Todo): string {
    const message = sdkInstance['NotificationsBL'].getMessage(<XRTodo>t);
    const title = this.titleCache[t.itemRef];
    return `${t.itemRef} ${title} ${message} ${t.login} ${t.originatorLogin}`;
  }

  public goToStart() {
    this.setPageStart(0);
  }

  public goToPreviousPage() {
    const nextIndex = Math.max(0, this.pageStart - this.pageLength);
    this.setPageStart(nextIndex);
  }

  public goToNextPage() {
    if (
      this.pageStart + this.pageLength <
      this.searchFilteredElementsLst.length
    ) {
      this.setPageStart(this.pageStart + this.pageLength);
    }
  }

  public goToLastPage() {
    const nextIndex =
      Math.floor(this.searchFilteredElementsLst.length / this.pageLength) *
      this.pageLength;
    if (nextIndex < this.searchFilteredElementsLst.length) {
      this.setPageStart(nextIndex);
    }
  }

  public getCurrentPage(): number {
    return Math.floor(this.pageStart / this.pageLength) + 1;
  }

  public getTotalPages(): number {
    const listLength = this.searchFilteredElementsLst.length;
    return Math.ceil(listLength / this.pageLength);
  }

  public isStartDisabled(): boolean {
    return this.getCurrentPage() === 1;
  }

  public isPreviousDisabled(): boolean {
    return this.getCurrentPage() === 1;
  }

  public isNextDisabled(): boolean {
    return (
      this.getTotalPages() == 0 || this.getCurrentPage() == this.getTotalPages()
    );
  }

  public isLastDisabled(): boolean {
    return (
      this.getTotalPages() == 0 || this.getCurrentPage() == this.getTotalPages()
    );
  }
}
